import { DownloadSimpleIcon } from "@phosphor-icons/react";
import { useState } from "react";
import { useIntl } from "react-intl";

import { Button } from "@/components/Button/Button";
import { CircularProgress } from "@/components/CircularProgress/CircularProgress";
import { showSnackbar } from "@/contexts/SnackbarContext";
import { useReservationDocuments } from "@/features/bookings/hooks/useReservationDocuments";
import type { FindUserReservationDocumentsDocument } from "@/types/codegen";

type DownloadButtonProps = {
  doc: FindUserReservationDocumentsDocument;
};

const ReservationDocumentDownloadButton = ({ doc }: DownloadButtonProps) => {
  const [isDownloading, setIsDownloading] = useState(false);

  const openLink = (doc: FindUserReservationDocumentsDocument) => {
    if (isDownloading) {
      return;
    }

    setIsDownloading(true);
    my?.downloadFile({
      url: doc.link,
      success({ apFilePath }) {
        my?.openDocument({
          filePath: apFilePath,
          fileType: "pdf",
        });
      },
      fail(res) {
        if (res.errorMessage) {
          showSnackbar({
            type: "error",
            content: res.errorMessage,
          });
        }
      },
      complete() {
        setIsDownloading(false);
      },
    });
  };

  return (
    <Button
      fullWidth
      size="small"
      variant="contained"
      // SuperQi has it's own api for displaying pdfs
      {...(my
        ? { onClick: () => openLink(doc) }
        : {
            href: doc.link,
            download: doc.name,
          })}
    >
      <span className="flex items-center gap-2">
        <span>Download {doc.name || "voucher"}</span>
        {isDownloading ? (
          <CircularProgress size={24} />
        ) : (
          <DownloadSimpleIcon size={24} />
        )}
      </span>
    </Button>
  );
};

type Props = {
  reservationId?: string;
};

export const ReservationDocumentList = ({ reservationId }: Props) => {
  const intl = useIntl();
  const { data } = useReservationDocuments(reservationId);

  if (!reservationId || !data?.length) {
    return (
      <div className="flex items-center justify-center gap-2">
        <CircularProgress size={20} />
        <span>{intl.formatMessage({ id: "preparingTickets" })}</span>
      </div>
    );
  }

  return (
    <ul>
      {data?.map((doc) => (
        <li key={doc.link} className="not-last:mb-2">
          <ReservationDocumentDownloadButton doc={doc} />
        </li>
      ))}
    </ul>
  );
};
